import { loadClinicalNoteAuthority, persistClinicalNote } from '@/services/clinicalNoteAuthority';
import { supabase } from "@/services/supabaseClient";
import { syncVitalRepVitalSigns } from "@/services/ehrVitalSignsService.js";

export const VITALREP_REPORT_TYPE = "vitalrep_v0_1";
export const VITALREP_CHANGED_EVENT = "afrimed:vitalrep:changed";
const VITALREP_LOCAL_PREFIX = "afrimed:vitalrep:";
export const VITALREP_VITALS_SOURCE = Object.freeze({
  NOT_CAPTURED: "not_captured",
  CLINICIAN_CAPTURED: "clinician_captured",
  PATIENT_REPORTED: "patient_reported",
  HOME_DEVICE: "home_device",
  UPLOADED_DOCUMENT: "uploaded_document",
});

export const EMPTY_VITALREP_STRUCTURED_CLINICAL_DATA = Object.freeze({
  vitals: Object.freeze({
    source: VITALREP_VITALS_SOURCE.NOT_CAPTURED,
    bloodPressure: "",
    heartRate: "",
    temperature: "",
    oxygenSaturation: "",
    respiratoryRate: "",
    weight: "",
  }),
  reasonForVisit: "",
  clinicalObservations: "",
  assessment: "",
  testsLabsRequested: "",
  medicationSummary: "",
  treatmentNextSteps: "",
  followUpAdvice: "",
});

function normalizeText(value) {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function normalizeNoteText(value) {
  return String(value || "")
    .replace(/\r\n?/g, "\n")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

// VitalRep™ notes are intentionally plain text. Bullets are stored as the
// canonical Unicode prefix so they remain readable wherever notes are shown.
export const VITALREP_BULLET_PREFIX = "• ";

export function normalizeVitalRepBulletLines(value = "") {
  return String(value || "").replace(/(^|\n)([ \t]*)[-*][ \t]+/g, `$1$2${VITALREP_BULLET_PREFIX}`);
}

export function getVitalRepBulletKeyEdit({ key, value = "", selectionStart = 0, selectionEnd = selectionStart }) {
  if (key !== "Enter") return null;

  const source = String(value || "");
  const start = Math.max(0, Math.min(Number(selectionStart) || 0, source.length));
  const end = Math.max(start, Math.min(Number(selectionEnd) || start, source.length));
  const lineStart = source.lastIndexOf("\n", start - 1) + 1;
  const lineBeforeCursor = source.slice(lineStart, start);
  const lineEnd = source.indexOf("\n", start);
  const line = source.slice(lineStart, lineEnd === -1 ? source.length : lineEnd);

  if (!lineBeforeCursor.startsWith(VITALREP_BULLET_PREFIX)) return null;

  if (line.trim() === VITALREP_BULLET_PREFIX.trim()) {
    const nextValue = `${source.slice(0, lineStart)}${source.slice(lineStart + VITALREP_BULLET_PREFIX.length)}`;
    return { value: nextValue, selectionStart: lineStart, selectionEnd: lineStart };
  }

  const nextValue = `${source.slice(0, start)}\n${VITALREP_BULLET_PREFIX}${source.slice(end)}`;
  const nextSelection = start + 1 + VITALREP_BULLET_PREFIX.length;
  return { value: nextValue, selectionStart: nextSelection, selectionEnd: nextSelection };
}

function makeVersionId(prefix = "version") {
  const stamp = new Date().toISOString();
  const random =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2);
  return `${prefix}-${stamp}-${random}`;
}

function normalizeSoapDraft(value = {}) {
  const source = value && typeof value === "object" ? value : {};
  return {
    subjective: normalizeNoteText(source.subjective),
    objective: normalizeNoteText(source.objective),
    assessment: normalizeNoteText(source.assessment),
    plan: normalizeNoteText(source.plan),
    unsorted: normalizeNoteText(source.unsorted),
    reviewWarning: normalizeNoteText(source.reviewWarning),
  };
}

function buildSoapDraftFromStructuredData(structuredClinicalData = {}) {
  const data = normalizeVitalRepStructuredClinicalData(structuredClinicalData);
  const objectiveParts = [
    data.vitals?.bloodPressure ? `Blood pressure: ${data.vitals.bloodPressure}` : "",
    data.vitals?.heartRate ? `Heart rate: ${data.vitals.heartRate}` : "",
    data.vitals?.temperature ? `Temperature: ${data.vitals.temperature}` : "",
    data.vitals?.oxygenSaturation ? `Oxygen saturation: ${data.vitals.oxygenSaturation}` : "",
    data.vitals?.respiratoryRate ? `Respiratory rate: ${data.vitals.respiratoryRate}` : "",
    data.vitals?.weight ? `Weight: ${data.vitals.weight}` : "",
    data.testsLabsRequested,
  ].filter(Boolean);
  return normalizeSoapDraft({
    subjective: [data.reasonForVisit, data.clinicalObservations].filter(Boolean).join("\n"),
    objective: objectiveParts.join("\n"),
    assessment: data.assessment,
    plan: [data.medicationSummary, data.treatmentNextSteps, data.followUpAdvice].filter(Boolean).join("\n"),
  });
}

function buildVitalRepPreviousVersionSnapshot({
  content = {},
  report = null,
  appointmentId = "",
  consultationReportId = "",
}) {
  const structuredData = normalizeVitalRepStructuredClinicalData(content.structuredClinicalData || {});
  const storedSoapDraft =
    content.soapDraft && typeof content.soapDraft === "object"
      ? normalizeSoapDraft(content.soapDraft)
      : buildSoapDraftFromStructuredData(structuredData);

  return {
    appointmentId: String(appointmentId || "").trim(),
    consultationReportId: String(consultationReportId || report?.id || "").trim(),
    subjective: storedSoapDraft.subjective,
    objective: storedSoapDraft.objective,
    assessment: storedSoapDraft.assessment,
    plan: storedSoapDraft.plan,
    unsorted: storedSoapDraft.unsorted,
    sourceTranscript: normalizeNoteText(content.sourceTranscript),
    generatedDraftText: normalizeNoteText(content.generatedDraftText),
    finalNoteText: normalizeNoteText(content.finalNoteText),
    status: normalizeText(content.status) || "finalized",
    finalizedAt: normalizeText(content.finalizedAt),
    generatedAt: normalizeText(content.generatedAt),
    createdAt: normalizeText(report?.created_at),
    updatedAt: normalizeText(report?.updated_at),
  };
}

function buildVitalRepEhrClinicalDocument({
  appointmentId,
  patientUserId,
  doctorUserId,
  consultationReportId,
  consultationId,
  soapDraft,
  sourceTranscript,
  status,
  finalizedAt,
  amendedAt = "",
}) {
  const normalizedSoapDraft = normalizeSoapDraft(soapDraft);
  return {
    documentType: "clinical_encounter_note",
    reportType: VITALREP_REPORT_TYPE,
    status: normalizeText(status) || "finalized",
    appointmentId: String(appointmentId || "").trim(),
    patientId: String(patientUserId || "").trim(),
    doctorId: String(doctorUserId || "").trim(),
    consultationReportId: String(consultationReportId || "").trim(),
    consultationId: String(consultationId || "").trim(),
    source: "vitalrep_beta",
    structuredSoap: {
      subjective: normalizedSoapDraft.subjective,
      objective: normalizedSoapDraft.objective,
      assessment: normalizedSoapDraft.assessment,
      plan: normalizedSoapDraft.plan,
      unsorted: normalizedSoapDraft.unsorted,
    },
    sourceTranscript: normalizeNoteText(sourceTranscript),
    finalizedAt: normalizeText(finalizedAt),
    amendedAt: normalizeText(amendedAt),
    updatedAt: new Date().toISOString(),
  };
}

function readLocalVitalRepRecord(appointmentId) {
  const cleanAppointmentId = String(appointmentId || "").trim();
  if (!cleanAppointmentId || typeof window === "undefined") return null;
  try {
    const raw =
      window.localStorage.getItem(`${VITALREP_LOCAL_PREFIX}${cleanAppointmentId}`) ||
      window.sessionStorage.getItem(`${VITALREP_LOCAL_PREFIX}${cleanAppointmentId}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeLocalVitalRepRecord(appointmentId, record) {
  const cleanAppointmentId = String(appointmentId || "").trim();
  if (!cleanAppointmentId || typeof window === "undefined" || !record) return;
  try {
    const payload = JSON.stringify(record);
    window.localStorage.setItem(`${VITALREP_LOCAL_PREFIX}${cleanAppointmentId}`, payload);
    window.sessionStorage.setItem(`${VITALREP_LOCAL_PREFIX}${cleanAppointmentId}`, payload);
  } catch {
    // Local fallback is best-effort only.
  }
}

function pickStructuredValue(source = {}, ...keys) {
  for (const key of keys) {
    if (source[key] !== undefined && source[key] !== null) return source[key];
  }
  return "";
}

function safeIsoDate(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  return date.toISOString();
}

function formatVisitDateTime(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  try {
    return new Intl.DateTimeFormat(undefined, {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  } catch {
    return safeIsoDate(date);
  }
}

function firstText(...values) {
  for (const value of values) {
    const next = normalizeText(value);
    if (next) return next;
  }
  return "";
}

function normalizeVitalsSource(value, vitals = {}) {
  const source = normalizeText(value)
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  const allowed = new Set(Object.values(VITALREP_VITALS_SOURCE));
  if (allowed.has(source)) return source;
  const hasLegacyVitals = [
    pickStructuredValue(vitals, "bloodPressure", "blood_pressure"),
    pickStructuredValue(vitals, "heartRate", "heart_rate"),
    vitals.temperature,
    pickStructuredValue(vitals, "oxygenSaturation", "oxygen_saturation"),
    pickStructuredValue(vitals, "respiratoryRate", "respiratory_rate"),
    vitals.weight,
  ].some((item) => normalizeText(item));
  return hasLegacyVitals
    ? ""
    : VITALREP_VITALS_SOURCE.NOT_CAPTURED;
}

function normalizeStructuredVitals(vitals = {}) {
  const source = vitals && typeof vitals === "object" ? vitals : {};
  return {
    source: normalizeVitalsSource(source.source, source),
    bloodPressure: normalizeText(pickStructuredValue(source, "bloodPressure", "blood_pressure")),
    heartRate: normalizeText(pickStructuredValue(source, "heartRate", "heart_rate")),
    temperature: normalizeText(source.temperature),
    oxygenSaturation: normalizeText(pickStructuredValue(source, "oxygenSaturation", "oxygen_saturation")),
    respiratoryRate: normalizeText(pickStructuredValue(source, "respiratoryRate", "respiratory_rate")),
    weight: normalizeText(source.weight),
  };
}

export function normalizeVitalRepStructuredClinicalData(value = {}) {
  const source = value && typeof value === "object" ? value : {};
  return {
    vitals: normalizeStructuredVitals(source.vitals),
    reasonForVisit: normalizeNoteText(pickStructuredValue(source, "reasonForVisit", "reason_for_visit")),
    clinicalObservations: normalizeNoteText(pickStructuredValue(source, "clinicalObservations", "clinical_observations")),
    assessment: normalizeNoteText(pickStructuredValue(source, "assessment", "diagnosis_assessment")),
    testsLabsRequested: normalizeNoteText(pickStructuredValue(source, "testsLabsRequested", "tests_labs_requested")),
    medicationSummary: normalizeNoteText(pickStructuredValue(source, "medicationSummary", "prescription_medication_summary")),
    treatmentNextSteps: normalizeNoteText(pickStructuredValue(source, "treatmentNextSteps", "treatment_next_steps")),
    followUpAdvice: normalizeNoteText(pickStructuredValue(source, "followUpAdvice", "follow_up_advice")),
  };
}

function toStoredStructuredClinicalData(value = {}) {
  const data = normalizeVitalRepStructuredClinicalData(value);
  return {
    vitals: {
      source: data.vitals.source,
      blood_pressure: data.vitals.bloodPressure,
      heart_rate: data.vitals.heartRate,
      temperature: data.vitals.temperature,
      oxygen_saturation: data.vitals.oxygenSaturation,
      respiratory_rate: data.vitals.respiratoryRate,
      weight: data.vitals.weight,
    },
    reason_for_visit: data.reasonForVisit,
    clinical_observations: data.clinicalObservations,
    diagnosis_assessment: data.assessment,
    tests_labs_requested: data.testsLabsRequested,
    prescription_medication_summary: data.medicationSummary,
    treatment_next_steps: data.treatmentNextSteps,
    follow_up_advice: data.followUpAdvice,
  };
}

export function hasVitalRepStructuredClinicalData(value = {}) {
  const data = normalizeVitalRepStructuredClinicalData(value);
  return Boolean(
    Object.entries(data.vitals).some(
      ([key, vitalValue]) => key !== "source" && Boolean(vitalValue)
    ) ||
      data.vitals.source !== VITALREP_VITALS_SOURCE.NOT_CAPTURED ||
      data.reasonForVisit ||
      data.clinicalObservations ||
      data.assessment ||
      data.testsLabsRequested ||
      data.medicationSummary ||
      data.treatmentNextSteps ||
      data.followUpAdvice
  );
}

export function buildInitialVitalRepStructuredClinicalData(sourceSnapshot = {}, prescriptionMeta = null) {
  const meta = prescriptionMeta && typeof prescriptionMeta === "object" ? prescriptionMeta : {};
  return normalizeVitalRepStructuredClinicalData({
    reasonForVisit: firstText(
      sourceSnapshot.serviceName,
      sourceSnapshot.consultationType,
      "Follow-up consultation"
    ),
    clinicalObservations: firstText(
      sourceSnapshot.diagnosisNotes,
      sourceSnapshot.clinicianNotes
    ),
    assessment: firstText(
      sourceSnapshot.diagnosisTitle,
      sourceSnapshot.diagnosisNotes
    ),
    medicationSummary: firstText(
      meta.medicineName,
      meta.medicine,
      meta.name,
      meta.title
    ),
    treatmentNextSteps: firstText(sourceSnapshot.clinicianNotes),
  });
}

export function buildVitalRepStructuredNoteText(structuredClinicalData = {}) {
  const data = normalizeVitalRepStructuredClinicalData(structuredClinicalData);
  const vitals = [
    ["Blood pressure", data.vitals.bloodPressure],
    ["Heart rate", data.vitals.heartRate],
    ["Temperature", data.vitals.temperature],
    ["Oxygen saturation", data.vitals.oxygenSaturation],
    ["Respiratory rate", data.vitals.respiratoryRate],
    ["Weight", data.vitals.weight],
  ]
    .filter(([, value]) => value)
    .map(([label, value]) => `${label}: ${value}`);
  const vitalsSourceText = {
    [VITALREP_VITALS_SOURCE.NOT_CAPTURED]: "Vitals not captured for this visit",
    [VITALREP_VITALS_SOURCE.CLINICIAN_CAPTURED]: "Clinician captured",
    [VITALREP_VITALS_SOURCE.PATIENT_REPORTED]: "Patient reported",
    [VITALREP_VITALS_SOURCE.HOME_DEVICE]: "Home device",
    [VITALREP_VITALS_SOURCE.UPLOADED_DOCUMENT]: "Uploaded document",
  }[data.vitals.source] || "Source not specified";
  const vitalsText =
    data.vitals.source === VITALREP_VITALS_SOURCE.NOT_CAPTURED
      ? vitalsSourceText
      : [vitalsSourceText, ...vitals].join("\n");

  return [
    ["Core vitals", vitalsText],
    ["Reason for visit", data.reasonForVisit],
    ["Clinical observations", data.clinicalObservations],
    ["Diagnosis and assessment", data.assessment],
    ["Tests & labs requested", data.testsLabsRequested],
    ["Prescription and medication summary", data.medicationSummary],
    ["Treatment and next steps", data.treatmentNextSteps],
    ["Follow-up advice", data.followUpAdvice],
  ]
    .filter(([, value]) => normalizeText(value))
    .map(([heading, value]) => `${heading}\n${value}`)
    .join("\n\n");
}

export function buildVitalRepSoapNoteText(soapDraft = {}) {
  const source = soapDraft && typeof soapDraft === "object" ? soapDraft : {};
  return [
    ["Subjective", source.subjective],
    ["Objective", source.objective],
    ["Assessment", source.assessment],
    ["Plan", source.plan],
    ["Unsorted clinical notes for review", source.unsorted],
  ]
    .map(([heading, value]) => [heading, normalizeNoteText(value)])
    .filter(([, value]) => value)
    .map(([heading, value]) => `${heading}\n${value}`)
    .join("\n\n");
}

function lineIncludesAny(line = "", terms = []) {
  const normalized = normalizeText(line).toLowerCase();
  return terms.some((term) => normalized.includes(term));
}

export function generateVitalRepSoapDraft(rawNotes = "") {
  const source = normalizeNoteText(rawNotes);
  const reviewWarning =
    "Doctor review required: verify, edit, and approve all content before final sign-off.";
  if (!source) {
    return {
      subjective: "Unsorted clinical notes for review: add patient-reported symptoms and history.",
      objective: "No objective findings, vitals, tests, or examination notes entered.",
      assessment: "No clinician impression explicitly stated in the source notes.",
      plan: "No treatment, prescription, tests, referrals, follow-up, or advice explicitly stated.",
      unsorted: "",
      reviewWarning,
    };
  }

  const buckets = {
    subjective: [],
    objective: [],
    assessment: [],
    plan: [],
    unsorted: [],
  };
  const lines = source
    .split(/\n+|(?<=[.!?])\s+(?=[A-Z0-9])/)
    .map((line) => normalizeText(line.replace(/^[-*•\d.)\s]+/, "")))
    .filter(Boolean);

  for (const line of lines) {
    if (
      lineIncludesAny(line, [
        "patient reports",
        "patient says",
        "complains",
        "symptom",
        "history",
        "pain",
        "fever",
        "cough",
        "nausea",
        "dizziness",
        "headache",
      ])
    ) {
      buckets.subjective.push(line);
      continue;
    }
    if (
      lineIncludesAny(line, [
        "bp",
        "blood pressure",
        "heart rate",
        "temperature",
        "oxygen",
        "spo2",
        "exam",
        "observed",
        "test",
        "lab",
        "result",
        "finding",
        "vital",
      ])
    ) {
      buckets.objective.push(line);
      continue;
    }
    if (
      lineIncludesAny(line, [
        "assessment",
        "impression",
        "diagnosis",
        "diagnosed",
        "likely",
        "consistent with",
      ])
    ) {
      buckets.assessment.push(line);
      continue;
    }
    if (
      lineIncludesAny(line, [
        "plan",
        "prescribe",
        "prescription",
        "start",
        "continue",
        "refer",
        "follow up",
        "follow-up",
        "advice",
        "recommend",
        "request",
        "order",
      ])
    ) {
      buckets.plan.push(line);
      continue;
    }
    buckets.unsorted.push(line);
  }

  return {
    subjective:
      buckets.subjective.join("\n") ||
      (buckets.unsorted.length ? buckets.unsorted.join("\n") : "No patient-reported symptoms or history clearly identified."),
    objective:
      buckets.objective.join("\n") ||
      "No objective findings, vitals, tests, or examination notes clearly identified.",
    assessment:
      buckets.assessment.join("\n") ||
      "No clinician impression explicitly stated in the source notes.",
    plan:
      buckets.plan.join("\n") ||
      "No treatment, prescription, tests, referrals, follow-up, or advice explicitly stated.",
    unsorted: buckets.subjective.length ? buckets.unsorted.join("\n") : "",
    reviewWarning,
  };
}

export function buildVitalRepSourceSnapshot({
  appointmentId,
  restoredSnapshot,
  diagnosisTitle,
  diagnosisNotes,
  notes,
}) {
  const restored = restoredSnapshot || {};
  const patient = restored?.patient || restored?.patientProfile || {};
  const doctor = restored?.doctor || restored?.doctorProfile || {};
  const consultationType = firstText(
    restored?.consultation_type,
    restored?.consultationType,
    restored?.visitType,
    restored?.mode
  );
  const serviceName = firstText(
    restored?.booked_service_name,
    restored?.serviceLabel,
    restored?.serviceName,
    restored?.specialty,
    restored?.reason,
    consultationType
  );
  const patientName = firstText(
    patient?.full_name,
    patient?.name,
    restored?.patientName,
    restored?.care_subject_name,
    "Patient"
  );
  const doctorName = firstText(
    doctor?.full_name,
    doctor?.name,
    restored?.doctor_name,
    restored?.doctorName,
    "Doctor"
  );
  const appointmentDate = firstText(
    restored?.appointment_date,
    restored?.when?.starts_at,
    restored?.appointmentDate,
    restored?.date
  );

  return {
    appointmentId: String(appointmentId || "").trim(),
    patientName,
    doctorName,
    consultationType,
    serviceName,
    appointmentDate,
    appointmentDateLabel: formatVisitDateTime(appointmentDate),
    diagnosisTitle: normalizeText(diagnosisTitle),
    diagnosisNotes: normalizeText(diagnosisNotes),
    clinicianNotes: normalizeText(notes),
  };
}

export function generateVitalRepDraftText(sourceSnapshot = {}) {
  const structuredClinicalData =
    sourceSnapshot?.structuredClinicalData && typeof sourceSnapshot.structuredClinicalData === "object"
      ? sourceSnapshot.structuredClinicalData
      : null;
  if (hasVitalRepStructuredClinicalData(structuredClinicalData)) {
    return buildVitalRepStructuredNoteText(structuredClinicalData);
  }

  const visitReason = firstText(
    sourceSnapshot.serviceName,
    sourceSnapshot.consultationType,
    "Follow-up consultation"
  );
  const visitContext = [sourceSnapshot.patientName, sourceSnapshot.appointmentDateLabel]
    .map((value) => normalizeText(value))
    .filter(Boolean)
    .join(" on ");
  const assessment = firstText(
    sourceSnapshot.diagnosisTitle,
    sourceSnapshot.diagnosisNotes,
    "Assessment recorded by clinician."
  );
  const clinicalNotes = firstText(
    sourceSnapshot.diagnosisNotes,
    sourceSnapshot.clinicianNotes,
    "Clinical observations were recorded during the consultation."
  );
  const immediatePlan = firstText(
    sourceSnapshot.clinicianNotes,
    "Continue with prescription, tests, and follow-up actions as needed."
  );

  return [
    "Visit reason",
    `${visitReason}${visitContext ? ` for ${visitContext}` : ""}.`,
    "",
    "Assessment",
    assessment.endsWith(".") ? assessment : `${assessment}.`,
    "",
    "Clinical notes",
    clinicalNotes.endsWith(".") ? clinicalNotes : `${clinicalNotes}.`,
    "",
    "Plan",
    immediatePlan.endsWith(".") ? immediatePlan : `${immediatePlan}.`,
  ].join("\n");
}

export function toVitalRepRecord(report = null) {
  if (!report) return null;
  const content = report?.content && typeof report.content === "object" ? report.content : {};
  return {
    id: String(report?.id || "").trim(),
    consultationId: String(report?.consultation_id || "").trim(),
    status: report.approved_by ? "finalized" : "draft",
    authorityReportId: report.approved_by ? report.id : null,
    noteVersion: report.note_version || null,
    supersedesReportId: report.supersedes_report_id || null,
    title: normalizeText(report?.title) || "VitalRep consultation note",
    generatedDraftText: normalizeNoteText(content.generatedDraftText),
    finalNoteText: normalizeNoteText(content.finalNoteText),
    sourceTranscript: normalizeNoteText(content.sourceTranscript),
    soapDraft: content.soapDraft && typeof content.soapDraft === "object"
      ? {
          subjective: normalizeNoteText(content.soapDraft.subjective),
          objective: normalizeNoteText(content.soapDraft.objective),
          assessment: normalizeNoteText(content.soapDraft.assessment),
          plan: normalizeNoteText(content.soapDraft.plan),
          unsorted: normalizeNoteText(content.soapDraft.unsorted),
          reviewWarning: normalizeNoteText(content.soapDraft.reviewWarning),
        }
      : null,
    structuredClinicalData: normalizeVitalRepStructuredClinicalData(content.structuredClinicalData),
    sourceSnapshot: content.sourceSnapshot && typeof content.sourceSnapshot === "object"
      ? content.sourceSnapshot
      : {},
    finalizedAt: normalizeText(content.finalizedAt),
    finalizedBy: normalizeText(content.finalizedBy),
    generatedBy: normalizeText(report?.generated_by),
    ehrClinicalDocument:
      content.ehrClinicalDocument && typeof content.ehrClinicalDocument === "object"
        ? content.ehrClinicalDocument
        : null,
    amendments: Array.isArray(content.amendments) ? content.amendments : [],
    updatedAt: firstText(report?.updated_at, report?.created_at),
    summary: normalizeText(report?.summary),
  };
}

export function buildVitalRepAuditTimeline(note = null) {
  if (!note) return [];
  const items = [];
  const finalizedAt = normalizeText(note.finalizedAt);
  if (finalizedAt) {
    items.push({
      id: "finalized",
      type: "finalized",
      label: "Finalised",
      timestamp: finalizedAt,
      clinician: normalizeText(note.finalizedBy || note.generatedBy),
      reason: "",
    });
  }
  (Array.isArray(note.amendments) ? note.amendments : []).forEach((item, index) => {
    const amendedAt = normalizeText(item?.amendedAt);
    items.push({
      id: normalizeText(item?.id) || `amended-${index}`,
      type: "amended",
      label: "Amended",
      timestamp: amendedAt,
      clinician: normalizeText(item?.amendedBy || item?.doctorId),
      reason: normalizeNoteText(item?.reason || item?.amendmentReason),
    });
  });
  return items.filter((item) => item.timestamp || item.reason);
}

async function getConsultationForAppointment(appointmentId) {
  const cleanAppointmentId = String(appointmentId || "").trim();
  if (!cleanAppointmentId) return null;

  const { data, error } = await supabase
    .from("consultations")
    .select("id, appointment_id, status, session_started_at, session_ends_at, updated_at")
    .eq("appointment_id", cleanAppointmentId)
    .order("updated_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error && error.code !== "PGRST116") throw error;
  return data || null;
}

async function ensureConsultationForAppointment(appointmentId, endedAt = "") {
  const cleanAppointmentId = String(appointmentId || "").trim();
  if (!cleanAppointmentId) throw new Error("Missing appointmentId");

  const existing = await getConsultationForAppointment(cleanAppointmentId);
  const completedAt = safeIsoDate(endedAt) || new Date().toISOString();

  if (existing?.id) {
    const nextPatch = {};
    if (String(existing.status || "").trim().toLowerCase() !== "completed") {
      nextPatch.status = "completed";
    }
    if (!existing.session_ends_at) {
      nextPatch.session_ends_at = completedAt;
    }
    if (Object.keys(nextPatch).length > 0) {
      await supabase.from("consultations").update(nextPatch).eq("id", existing.id);
    }
    return String(existing.id).trim();
  }

  const { data, error } = await supabase
    .from("consultations")
    .insert({
      appointment_id: cleanAppointmentId,
      status: "completed",
      session_ends_at: completedAt,
    })
    .select("id")
    .single();

  if (error) throw error;
  return String(data?.id || "").trim();
}

async function getVitalRepReportByConsultation(consultationId) {
  const cleanConsultationId = String(consultationId || "").trim();
  if (!cleanConsultationId) return null;

  const { data, error } = await supabase
    .from("consultation_reports")
    .select("id, consultation_id, generated_by, report_type, title, content, summary, created_at, updated_at")
    .eq("consultation_id", cleanConsultationId)
    .eq("report_type", VITALREP_REPORT_TYPE)
    .order("updated_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error && error.code !== "PGRST116") throw error;
  return data || null;
}

function formatTranscriptLine(row = {}) {
  const speaker = normalizeText(row.speaker_name || row.speaker_type);
  const content = normalizeNoteText(row.content || row.transcript_text || row.text || row.message);
  if (!content) return "";
  return speaker ? `${speaker}: ${content}` : content;
}

async function getVideoSessionForAppointment(appointmentId) {
  const cleanAppointmentId = String(appointmentId || "").trim();
  if (!cleanAppointmentId) return null;
  const { data, error } = await supabase
    .from("appointment_video_sessions")
    .select("id, appointment_id, status, transcription_status, transcription_ref, updated_at, created_at")
    .eq("appointment_id", cleanAppointmentId)
    .order("updated_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error && error.code !== "PGRST116") return null;
  return data || null;
}

export async function fetchVitalRepTranscriptRoughNotes({ appointmentId, sessionId } = {}) {
  const cleanAppointmentId = String(appointmentId || "").trim();
  const cleanSessionId = String(sessionId || "").trim();
  if (!cleanAppointmentId && !cleanSessionId) return "";

  const consultation = cleanAppointmentId ? await getConsultationForAppointment(cleanAppointmentId) : null;
  const consultationIds = Array.from(
    new Set(
      [
        consultation?.id,
        cleanSessionId && !cleanSessionId.startsWith("avs_") ? cleanSessionId : "",
      ]
        .map((value) => String(value || "").trim())
        .filter(Boolean)
    )
  );

  for (const consultationId of consultationIds) {
    const { data, error } = await supabase
      .from("consultation_transcripts")
      .select("id, consultation_id, speaker_type, speaker_name, timestamp_in_session, content, created_at")
      .eq("consultation_id", consultationId)
      .order("timestamp_in_session", { ascending: true });
    if (!error && Array.isArray(data) && data.length) {
      return data.map(formatTranscriptLine).filter(Boolean).join("\n");
    }
  }

  const videoSession = cleanSessionId ? null : await getVideoSessionForAppointment(cleanAppointmentId);
  const transcriptRef = normalizeText(videoSession?.transcription_ref);
  if (transcriptRef && !consultationIds.includes(transcriptRef)) {
    const { data, error } = await supabase
      .from("consultation_transcripts")
      .select("id, consultation_id, speaker_type, speaker_name, timestamp_in_session, content, created_at")
      .eq("consultation_id", transcriptRef)
      .order("timestamp_in_session", { ascending: true });
    if (!error && Array.isArray(data) && data.length) {
      return data.map(formatTranscriptLine).filter(Boolean).join("\n");
    }
  }

  return "";
}

function buildVitalRepPayload({
  consultationId,
  doctorUserId,
  existingReport,
  sourceSnapshot,
  generatedDraftText,
  finalNoteText,
  structuredClinicalData,
  status,
}) {
  const content = existingReport?.content && typeof existingReport.content === "object"
    ? existingReport.content
    : {};
  const nextGeneratedDraftText = normalizeNoteText(generatedDraftText);
  const nextFinalNoteText = normalizeNoteText(finalNoteText);
  const nextStructuredClinicalData = normalizeVitalRepStructuredClinicalData(structuredClinicalData);
  const nextStoredStructuredClinicalData = toStoredStructuredClinicalData(nextStructuredClinicalData);
  const nextStatus = normalizeText(status) || "draft_ready";

  return {
    id: existingReport?.id || undefined,
    consultation_id: consultationId,
    generated_by: doctorUserId,
    report_type: VITALREP_REPORT_TYPE,
    title: "VitalRep consultation note",
    content: {
      ...content,
      version: "v0.1",
      status: nextStatus,
      sourceSnapshot,
      structuredClinicalData: nextStoredStructuredClinicalData,
      generatedDraftText: nextGeneratedDraftText,
      finalNoteText: nextFinalNoteText,
      generatedAt: content.generatedAt || new Date().toISOString(),
      finalizedAt: nextStatus === "finalized" ? new Date().toISOString() : content.finalizedAt || null,
      finalizedBy: nextStatus === "finalized" ? doctorUserId : content.finalizedBy || null,
    },
    summary: normalizeText(nextStatus === "finalized"
      ? nextFinalNoteText.slice(0, 240)
      : nextGeneratedDraftText.slice(0, 240)),
    recommendations: null,
    follow_up_required: false,
    follow_up_date: null,
  };
}

export async function loadVitalRepNoteByAppointment(appointmentId) {
  const authority=await loadClinicalNoteAuthority(appointmentId);
  return toVitalRepRecord(authority.final_report || authority.draft_report);
}

async function saveGovernedVitalRep(options,finalise=false,amendment=false) {
 const {appointmentId,sourceSnapshot,sourceTranscript,soapDraft,structuredClinicalData,noteText,canonicalContent}=options;
 const authority=await loadClinicalNoteAuthority(appointmentId);
 const previous=authority.final_report||authority.draft_report;
 if(finalise&&authority.final_report&&!amendment)throw new Error('Create an amendment to change the finalised clinical note.');
 const structured=normalizeVitalRepStructuredClinicalData(structuredClinicalData||previous?.content?.structuredClinicalData||{});
 const soap=soapDraft||(structuredClinicalData?buildSoapDraftFromStructuredData(structured):previous?.content?.soapDraft)||buildSoapDraftFromStructuredData(structured);
 const text=(soapDraft||structuredClinicalData?buildVitalRepSoapNoteText(soap):normalizeNoteText(noteText))||buildVitalRepSoapNoteText(soap)||buildVitalRepStructuredNoteText(structured);
 const generation={...previous?.content?.provenance,...options.generationProvenance};
 for(const key of ['sourceMode','sourceTranscriptReference','generationMethod','modelIdentifier','promptTemplateVersion'])if(options[key]!==undefined)generation[key]=options[key];
 const content={...previous?.content,clinicianNotes:options.clinicianNotes??previous?.content?.clinicianNotes??'',sourceSnapshot:sourceSnapshot||previous?.content?.sourceSnapshot||{},sourceTranscript:sourceTranscript??previous?.content?.sourceTranscript??'',soapDraft:soap,structuredClinicalData:structured,generatedDraftText:text,finalNoteText:finalise?text:'',...(canonicalContent?{canonicalContent}:{}),provenance:{...generation,sourceReportId:previous?.id||null,sourceAppointmentId:appointmentId},generatedAt:new Date().toISOString()};
 const saved=await persistClinicalNote({appointmentId,content,finalise,expectedFinal:amendment?(options.expectedFinalReportId||null):(authority.final_report?.id||null),amendmentReason:amendment?options.amendmentReason:null});
 if(finalise&&hasVitalRepStructuredClinicalData(structured)) {
  try {await syncVitalRepVitalSigns({patientId:options.patientUserId,doctorId:options.doctorUserId,appointmentId,consultationReportId:saved.id,vitals:structured.vitals,capturedAt:saved.approved_at,notes:text});}
  catch(error){console.warn('vitalrep_vital_signs_sync_failed',{consultationReportId:saved.id,message:error?.message});}
 }
 const record=toVitalRepRecord(saved);
 if(typeof window!=='undefined')window.dispatchEvent(new Event(VITALREP_CHANGED_EVENT));
 return record;
}
export async function upsertVitalRepDraftForAppointment(options) {
 const authority=await loadClinicalNoteAuthority(options.appointmentId);
 if(authority.final_report)return toVitalRepRecord(authority.final_report);
 return saveGovernedVitalRep(options);
}
export async function saveVitalRepFinalNote(options){return saveGovernedVitalRep(options,true);}
export async function saveVitalRepAmendmentForAppointment(options){
 if(!String(options.amendmentReason||'').trim())throw new Error('Missing amendment reason');
 return saveGovernedVitalRep(options,true,true);
}
export async function saveVitalRepBetaNoteForAppointment(options){return saveGovernedVitalRep(options,['final','finalized'].includes(options.status));}
